import { useRef, useState } from 'react';

/**
 * Choosing a product photo.
 *
 * The file is shrunk in the browser before it is ever sent — a phone photo is
 * several megabytes and a catalogue thumbnail needs to be a few hundred
 * kilobytes at most, so it is drawn onto a canvas at a sane size and re-encoded.
 * That keeps it under the column's ceiling without asking a shopkeeper to
 * understand image sizes, and it previews exactly what will be stored.
 */
export function ImagePicker({
  value,
  onChange,
  stacked,
}: {
  value: string | null;
  onChange: (dataUri: string | null) => void;
  /** Photo above its button, for a narrow column beside a form. */
  stacked?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async (file: File): Promise<void> => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('That is not an image.');
      return;
    }
    setBusy(true);
    try {
      const dataUri = await shrink(file);
      onChange(dataUri);
    } catch {
      setError('Could not read that image. Try another.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={stacked ? 'flex flex-col items-stretch gap-2' : 'flex items-center gap-3'}>
      <div
        className={`grid shrink-0 place-items-center overflow-hidden rounded-md border border-silver-100 bg-silver-50 ${
          stacked ? 'aspect-square w-full' : 'h-20 w-20'
        }`}
      >
        {value ? (
          <img src={value} alt="Product" className="h-full w-full object-cover" />
        ) : (
          <span className="text-2xs text-ink-faint">No photo</span>
        )}
      </div>

      <div className={stacked ? 'flex flex-col items-stretch gap-1 text-center' : 'space-y-1'}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handle(file);
          }}
        />
        <button
          type="button"
          className={`btn-ghost py-1 text-xs ${stacked ? 'w-full' : ''}`}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Reading…' : value ? 'Change photo' : 'Add photo'}
        </button>
        {value ? (
          <button
            type="button"
            className={`${stacked ? '' : 'block'} text-2xs text-danger underline`}
            onClick={() => onChange(null)}
          >
            Remove
          </button>
        ) : null}
        {error ? <p className="text-2xs text-danger">{error}</p> : null}
      </div>
    </div>
  );
}

/** Draw the image onto a canvas at most 600px on its long side, re-encode as JPEG. */
export async function shrink(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const max = 600;
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bitmap, 0, 0, w, h);

  // Step the quality down until it fits comfortably under the column's ceiling.
  for (const quality of [0.82, 0.7, 0.6, 0.5]) {
    const uri = canvas.toDataURL('image/jpeg', quality);
    if (uri.length <= 1_300_000) return uri;
  }
  return canvas.toDataURL('image/jpeg', 0.4);
}
